import { IconButton, TextButton } from '@creditornot/cb-button';
import { DataTable } from '@creditornot/cb-data-table';
import { CaretDown, CheckmarkCircleFilled, CrossCircleFilled, Download } from '@creditornot/cb-icons';
import { typographyCss, cbSemanticCssVars, colors, WoltLogo } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { DateTime } from 'luxon';
import { unparse } from 'papaparse';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { downloadEncodedCsv, localize } from 'common/utils';

import { FilesMatch } from '../types';
import { normalizeString } from '../utils';

type Props = {
  className?: string;
  data: FilesMatch[];
  primaryLanguage: Language;
};

const Root = styled.div`
  margin-block-start: 16px;
`;

const Toolbar = styled.div`
  ${typographyCss.Body3};
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledTextButton = styled(TextButton)`
  margin-inline-start: 8px;
`;

const PopoverContent = styled(MenuItemGroup)`
  min-width: 224px;
  padding: 0 8px;
`;

const ImageNameContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  margin: 8px 0;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  overflow: hidden;
`;

const Thumbnail = styled.img`
  width: 36px;
  height: 36px;
  object-fit: cover;
`;

const ImageName = styled.div`
  flex: 1;
  margin-inline-start: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlaceholderImage = styled(WoltLogo)`
  width: 26px;
  fill: ${cbSemanticCssVars.textTertiary};
`;

const ItemName = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  :not(:first-child) {
    margin-block-start: 4px;
  }
`;

const ErrorIcon = styled(CrossCircleFilled)`
  display: block;
  width: 24px;
  fill: ${colors.strawberry100};
`;

const SuccessIcon = styled(CheckmarkCircleFilled)`
  display: block;
  width: 24px;
  fill: ${colors.lime100};
`;

const statusToTranslation = {
  all: 'menu.bulk-image.status-all',
  failure: 'menu.bulk-image.status-failure',
  success: 'menu.bulk-image.status-success',
};

const StatusTable = ({ data, className, primaryLanguage }: Props) => {
  const intl = useIntl();

  const [listBy, setListBy] = useState<'all' | 'failure' | 'success'>('all');
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const filteredResults = useMemo(
    () => (listBy === 'all' ? data : data?.filter(file => file.status === listBy)),
    [listBy, data],
  );

  const paginatedResults = useMemo(() => {
    const startIndex = (activePage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredResults.slice(startIndex, endIndex);
  }, [activePage, filteredResults, pageSize]);

  const downloadResults = useCallback(() => {
    const downloadableResults = filteredResults.map(r => ({
      'Images names': r.files.map(({ name }) => name),
      'Matched item names': r.matchedOffers?.map(item => localize(item.name, primaryLanguage)).join('\n') || '-',
      Status: r.status,
      Note: r.note,
    }));
    const csvResults = unparse(downloadableResults || []);
    const filename = `file-matching-results-${DateTime.local().toISODate()}.csv`;
    downloadEncodedCsv(csvResults, 'text/csv', filename, primaryLanguage === 'ja');
  }, [filteredResults, primaryLanguage]);

  const handleStatusChange = (status: 'all' | 'failure' | 'success') => {
    setActivePage(1);
    setListBy(status);
  };

  const pageCount = useMemo(() => Math.ceil(filteredResults.length / pageSize), [filteredResults.length, pageSize]);

  return (
    <Root className={className}>
      <Toolbar>
        <div>
          <FormattedMessage id="menu.bulk-image.status" />:{' '}
          <Popover
            showOnClick
            showOnHover={false}
            renderArrow={false}
            portalElement={null}
            contentZIndex={1}
            placement="bottom-start"
            closeOnClickInside
            content={
              <PopoverContent>
                {Object.keys(statusToTranslation).map((key: keyof typeof statusToTranslation) => (
                  <MenuActionItem
                    key={key}
                    variant="black"
                    selected={listBy === key}
                    onClick={() => handleStatusChange(key)}
                  >
                    <FormattedMessage id={statusToTranslation[key]} />
                  </MenuActionItem>
                ))}
              </PopoverContent>
            }
          >
            <StyledTextButton variant="blue" icon={<CaretDown />} iconPosition="right">
              <FormattedMessage id={statusToTranslation[listBy]} />
            </StyledTextButton>
          </Popover>
        </div>
        <IconButton variant="black" size="small" onClick={downloadResults} disabled={!filteredResults.length}>
          <Download />
        </IconButton>
      </Toolbar>

      <DataTable<FilesMatch>
        getRowKey={record => record.files[0].name}
        dataSource={paginatedResults || []}
        tableLayout="fixed"
        pagination={
          pageCount > 0
            ? {
                activePage,
                onPageChange: setActivePage,
                pageCount,
                pageSize,
                onPageSizeChange: setPageSize,
              }
            : undefined
        }
        columns={[
          {
            id: 'image-name',
            name: intl.formatMessage({ id: 'menu.bulk-image.image-name' }),
            style: { width: '35%' },
            render: ({ record }) =>
              record.files.map(file => (
                <ImageNameContainer>
                  <ImageContainer>
                    {record.previews?.[file.name] ? (
                      <Thumbnail src={record.previews?.[file.name]} />
                    ) : (
                      <PlaceholderImage />
                    )}
                  </ImageContainer>
                  <ImageName title={file.name}>{normalizeString(file.name)}</ImageName>
                </ImageNameContainer>
              )),
          },
          {
            id: 'matched-item-names',
            name: intl.formatMessage({ id: 'menu.bulk-image.matched-item-names' }),
            style: { width: '35%' },
            render: ({ record }) =>
              record.matchedOffers?.map(i => {
                const itemName = localize(i.name, primaryLanguage);
                return (
                  <ItemName key={i.id} title={itemName}>
                    {itemName}
                  </ItemName>
                );
              }) || '-',
          },
          {
            id: 'note',
            name: intl.formatMessage({ id: 'menu.bulk-image.note' }),
            render: ({ record }) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginInlineEnd: 8 }}>
                  {record.status === 'success' ? <SuccessIcon /> : <ErrorIcon />}
                </div>{' '}
                {record.note}
              </div>
            ),
          },
        ]}
      />
    </Root>
  );
};

export default StatusTable;
